import { supabase } from '../lib/supabase';

/**
 * Service for managing verification workflows with real-time status tracking
 * Handles access request verification, history tracking, and real-time subscriptions
 */

// Transform database row (snake_case) to React object (camelCase)
const transformFromDB = (row) => {
  if (!row) return null;
  
  return {
    id: row?.id,
    fullName: row?.full_name,
    email: row?.email,
    role: row?.role,
    bio: row?.bio,
    experience: row?.experience,
    industry: row?.industry,
    seniority: row?.seniority,
    language: row?.language,
    location: row?.location,
    companyName: row?.company_name,
    companyWebsite: row?.company_website,
    linkedinUrl: row?.linkedin_url,
    status: row?.status,
    qualificationScore: row?.qualification_score,
    adminNotes: row?.admin_notes,
    followUpStatus: row?.follow_up_status,
    submittedAt: row?.submitted_at,
    updatedAt: row?.updated_at,
    reviewedAt: row?.reviewed_at,
    reviewedBy: row?.reviewed_by,
    verificationNotes: row?.verification_notes,
    verificationScore: row?.verification_score,
    riskLevel: row?.risk_level,
    lastStatusChange: row?.last_status_change,
    rejectionReason: row?.rejection_reason,
    approvedByName: row?.approved_by_name
  };
};

// Transform verification history from DB
const transformHistoryFromDB = (row) => {
  if (!row) return null;
  
  return {
    id: row?.id,
    accessRequestId: row?.access_request_id,
    previousStatus: row?.previous_status,
    newStatus: row?.new_status,
    changedBy: row?.changed_by,
    changedByName: row?.changed_by_name,
    notes: row?.notes,
    createdAt: row?.created_at
  };
};

export const verificationService = {
  /**
   * Get all access requests with real-time updates
   */
  async getAllRequests() {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.select('*')
        ?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching access requests:', error);
      throw error;
    }
  },

  /**
   * Get requests by status
   */
  async getRequestsByStatus(status) {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.select('*')
        ?.eq('status', status)
        ?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching requests by status:', error);
      throw error;
    }
  },

  /**
   * Get requests by role
   */
  async getRequestsByRole(role) {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.select('*')
        ?.eq('role', role)
        ?.order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformFromDB) || [];
    } catch (error) {
      console.error('Error fetching requests by role:', error);
      throw error;
    }
  },

  /**
   * Approve access request
   */
  async approveRequest(requestId, adminNotes = '', adminId = null, adminName = '') {
    try {
      console.log('💾 Database RPC - Approving request:', { requestId, adminNotes, adminId, adminName });
      
      // Use SECURITY DEFINER function to bypass RLS
      const { data, error } = await supabase
        ?.rpc('admin_approve_access_request', {
          request_id: requestId,
          admin_notes_param: adminNotes,
          admin_id_param: adminId,
          admin_name_param: adminName
        });
      
      if (error) {
        console.error('❌ Database error during approval:', error);
        throw error;
      }
      
      console.log('✅ Database response:', data);
      return transformFromDB(data?.[0]);
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  },

  /**
   * Reject access request
   */
  async rejectRequest(requestId, rejectionReason = '', adminNotes = '', adminId = null) {
    try {
      console.log('💾 Database RPC - Rejecting request:', { requestId, rejectionReason, adminNotes, adminId });
      
      // Use SECURITY DEFINER function to bypass RLS
      const { data, error } = await supabase
        ?.rpc('admin_reject_access_request', {
          request_id: requestId,
          rejection_reason_param: rejectionReason,
          admin_notes_param: adminNotes,
          admin_id_param: adminId
        });
      
      if (error) {
        console.error('❌ Database error during rejection:', error);
        throw error;
      }
      
      console.log('✅ Database response:', data);
      console.log('✅ Updated status:', data?.[0]?.status);
      return transformFromDB(data?.[0]);
    } catch (error) {
      console.error('Error rejecting request:', error);
      throw error;
    }
  },

  /**
   * Update request status to under review
   */
  async markUnderReview(requestId, adminId = null, adminName = '') {
    try {
      // Use SECURITY DEFINER function to bypass RLS
      const { data, error } = await supabase
        ?.rpc('admin_mark_under_review', {
          request_id: requestId,
          admin_id_param: adminId,
          admin_name_param: adminName
        });
      
      if (error) throw error;
      return transformFromDB(data?.[0]);
    } catch (error) {
      console.error('Error marking request under review:', error);
      throw error;
    }
  },

  /**
   * Update verification notes
   */
  async updateVerificationNotes(requestId, notes) {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.update({ verification_notes: notes })
        ?.eq('id', requestId)
        ?.select();
      
      if (error) throw error;
      return transformFromDB(data?.[0]);
    } catch (error) {
      console.error('Error updating verification notes:', error);
      throw error;
    }
  },

  /**
   * Update risk level
   */
  async updateRiskLevel(requestId, riskLevel) {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.update({ risk_level: riskLevel })
        ?.eq('id', requestId)
        ?.select();
      
      if (error) throw error;
      return transformFromDB(data?.[0]);
    } catch (error) {
      console.error('Error updating risk level:', error);
      throw error;
    }
  },

  /**
   * Get verification history for a request
   */
  async getVerificationHistory(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('verification_history')
        ?.select('*')
        ?.eq('access_request_id', requestId)
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(transformHistoryFromDB) || [];
    } catch (error) {
      console.error('Error fetching verification history:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time access request changes
   */
  subscribeToRequests(callback) {
    const channel = supabase
      ?.channel('access_requests_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'access_requests'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to verification history changes
   */
  subscribeToHistory(requestId, callback) {
    const channel = supabase
      ?.channel(`verification_history_${requestId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'verification_history',
          filter: `access_request_id=eq.${requestId}`
        },
        (payload) => {
          callback(transformHistoryFromDB(payload?.new));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time channel
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Get verification statistics
   */
  async getVerificationStats() {
    try {
      const { data, error } = await supabase
        ?.from('access_requests')
        ?.select('status, role');
      
      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(r => r?.status === 'pending')?.length || 0,
        underReview: data?.filter(r => r?.status === 'under_review')?.length || 0,
        approved: data?.filter(r => r?.status === 'approved')?.length || 0,
        rejected: data?.filter(r => r?.status === 'rejected')?.length || 0,
        candidates: data?.filter(r => r?.role === 'candidate')?.length || 0,
        employers: data?.filter(r => r?.role === 'employer')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching verification stats:', error);
      throw error;
    }
  }
};

export default verificationService;